import { useState, useCallback } from 'react'
import {
  FooterContainer,
  AccordionContent,
  RatingsList,
} from './styles/StakeholderRatingsFooterStyles'
import { useStakeholderLookup } from './hooks/useStakeholderLookup'
import { useScopedChatHandler } from './hooks/useScopedChatHandler'
import { EmptyState } from './components/EmptyState'
import { AccordionHeader } from './components/AccordionHeader'
import { RaterCard } from './components/RaterCard'
import { StakeholderCTAList } from './components/StakeholderCTAList'
import { calculateAverageRating } from './utils'
import type { StakeholderRatingsFooterProps } from './types'

/**
 * Footer component for displaying stakeholder ratings on idea nodes.
 * Shows average score in collapsed state, expands to show individual ratings.
 */
export function StakeholderRatingsFooter({
  ratings,
  categoryStyle,
  onGenerateRatings,
  generatingNames = [],
  ideaLabel = 'this idea',
}: StakeholderRatingsFooterProps): React.JSX.Element {
  const isGeneratingAny = generatingNames.length > 0
  const [isExpanded, setIsExpanded] = useState(false)
  const {
    projectStakeholders,
    characterModal,
    getStakeholderPortraitUrl,
    getStakeholderTemplateId,
    handleAvatarClick,
  } = useStakeholderLookup()
  const { handleOpenScopedChat, hasChatContext } = useScopedChatHandler(ideaLabel)

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  // Determine rating state — only stakeholder ratings are surfaced.
  const hasStakeholderRatings = (ratings?.stakeholders.length ?? 0) > 0
  const hasAnyRatings = hasStakeholderRatings

  // No ratings — show empty state
  if (!ratings || !hasAnyRatings) {
    return <EmptyState categoryStyle={categoryStyle} />
  }

  const averageRating = calculateAverageRating(ratings)
  const ratingLabel = 'Team rating:'

  return (
    <FooterContainer $borderColor={categoryStyle.borderColor}>
      <AccordionHeader
        ratingLabel={ratingLabel}
        averageRating={averageRating}
        isExpanded={isExpanded}
        isGenerating={isGeneratingAny}
        categoryStyle={categoryStyle}
        onToggle={handleToggle}
      />

      <AccordionContent $isOpen={isExpanded}>
        <RatingsList>
          {/* Stakeholder Ratings */}
          {ratings.stakeholders.map((stakeholder, index) => {
            const templateId = getStakeholderTemplateId(stakeholder.name)
            return (
              <RaterCard
                key={index}
                rater={stakeholder}
                roleLabel={stakeholder.role}
                categoryStyle={categoryStyle}
                hasChatContext={hasChatContext}
                portraitUrl={getStakeholderPortraitUrl(stakeholder.name)}
                onAvatarClick={templateId ? () => handleAvatarClick(templateId) : undefined}
                onOpenChat={(feedbackType, content) =>
                  handleOpenScopedChat(stakeholder, feedbackType, content)
                }
              />
            )
          })}

          {/* CTAs for unrated stakeholders + add team */}
          <StakeholderCTAList
            ratings={ratings}
            categoryStyle={categoryStyle}
            generatingNames={generatingNames}
            projectStakeholders={projectStakeholders}
            onGenerateRatings={onGenerateRatings}
            characterModal={characterModal}
          />
        </RatingsList>
      </AccordionContent>
    </FooterContainer>
  )
}
